import { selectedLayersAtom } from '@/atoms';
import { selectedFeatureAtom } from '@/atoms/selection.atom';
import { clearHistoryAtom } from '@/atoms/snapping.atom';
import { enterPipeEditModeAtom } from '@/atoms/pipe.atom';
import { parsePipeProperties } from '@/lib/pipe/feature';
import type { Vec3 } from '@/lib/pipe/types';
import { enterStockpileEditModeAtom, stockpileToolModeAtom } from '@/atoms/stockpile.atom';
import {
  alignmentDrapeSourcesAtom,
  alignmentWalkAtom,
  alignmentsAtom,
  DEFAULT_WALK_STEP,
  selectedAlignmentKeyAtom,
} from '@/atoms/alignment.atom';
import { layersAtom } from '@/atoms/layers.atom';
import { parseFeatureAlignmentProps, type FeatureAlignmentProps } from '@/core/featureAlignment';
import {
  addLineModeAtom,
  addPointModeAtom,
  addPolygonModeAtom,
  addSurfaceModeAtom,
  DEFAULT_ADD_SURFACE_MODE,
  pointCloudTargetsAtom,
  viewerEvents,
} from '@/atoms/viewer.atom';
import { getMaterial } from '@/lib/stockpile/materials';
import {
  parseStockpileProperties,
  type StockpileFeatureProperties,
} from '@/lib/stockpile/feature';
import { RainfallFeatureView, parseRainfallFeature } from './RainfallFeatureView';
import { formatPropertyValue } from '@/lib/formatPropertyValue';
import { getPlaceholderAvatar } from '@/core/avatar';
import { propertyBlacklist } from '@/core/config';
import { execute } from '@/graphql/execute';
import { FeatureAttachmentsQuery } from '@/graphql/graphql';
import { useFeatureCheck } from '@/hooks/useFeature';
import { useOrganizations } from '@/hooks/useOrganizations';
import { createAttachment } from '@/queries/attachment.query';
import {
  createFeatureAttachmentMutation,
  deleteFeatureAttachmentMutation,
  deleteFeatureMutation,
  featureAttachmentsQuery,
  featureQuery,
} from '@/queries/feature.query';
import { Download, InfoCircleSolid, LabelSolid, Pencil, Plus, Trash } from '@mynaui/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Check, ChevronsUpDown, Crosshair, Loader2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FunctionComponent, type ReactNode } from 'react';
import { analytics, useFeatureFlagEnabled } from '@/lib/analytics';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { loadFeaturesByLayerOfflineAware } from '@/lib/offline/offlineFeatures';
import { formatDateTime } from './BatchList';
import { Coords } from './Coords';
import { FeatureNavigator } from './FeatureNavigator';
import { ID } from './ID';
import PortalTooltip from './PortalTooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { NumericField } from './ui/numeric-field';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { cn } from '@/lib/utils';

interface iFeaturePropertiesProps {}

type FeatureAttachment = FeatureAttachmentsQuery['featureAttachments'][number];

const FeatureProperties: FunctionComponent<iFeaturePropertiesProps> = () => {
  const { hasRole } = useOrganizations();
  const { projectId } = useParams();
  const [selectedFeature, setSelectedFeature] = useAtom(selectedFeatureAtom);
  const clearHistory = useSetAtom(clearHistoryAtom);
  const setSelectedAlignmentKey = useSetAtom(selectedAlignmentKeyAtom);
  const [addSurfaceMode, setAddSurfaceMode] = useAtom(addSurfaceModeAtom);
  const addPointMode = useAtomValue(addPointModeAtom);
  const addLineMode = useAtomValue(addLineModeAtom);
  const addPolygonMode = useAtomValue(addPolygonModeAtom);
  const layers = useAtomValue(selectedLayersAtom(projectId!));
  const layer = layers.find((layer) => layer.id === selectedFeature?.layerId);
  const queryClient = useQueryClient();
  const { t } = useTranslation('components');
  // Stockpile keys live on the 'project' namespace (the source of truth for
  // every other stockpile UI string in the app). Use a separate `t` so we can
  // reference them without polluting the components namespace.
  const { t: tStockpile } = useTranslation('project');
  const enterStockpileEditMode = useSetAtom(enterStockpileEditModeAtom);
  const enterPipeEditMode = useSetAtom(enterPipeEditModeAtom);
  const setStockpileToolMode = useSetAtom(stockpileToolModeAtom);
  // PostHog flag — gates every stockpile UI surface in this drawer
  // (Re-compute on saved stockpiles AND Calculate-from-polygon). Coerce
  // undefined → false so flag-off users don't see a flash of the buttons
  // while PostHog hydrates.
  const isStockpileAnalysisEnabled = !!useFeatureFlagEnabled('stockpileAnalysis');
  // Subscription gate check — imperative form so we can branch inside handlers.
  const featureCheck = useFeatureCheck();
  // Live atom of actually-loaded PCOs (see comment in _handleCalculateFromPolygon).
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);

  // Recursive function to get all parent layers upward in the ancestry tree, starting from feature's parent layer
  const getLayerAncestry = (currentLayer: typeof layer | undefined, allLayers: typeof layers): (typeof layer)[] => {
    if (!currentLayer || !currentLayer.parentLayerId) {
      return [];
    }
    const parentLayer = allLayers.find((l) => l.id === currentLayer.parentLayerId);
    if (!parentLayer) {
      return [];
    }
    // Traverse upward, accumulating parents
    return [parentLayer, ...getLayerAncestry(parentLayer, allLayers)];
  };
  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Detect if this is a draft feature (sub-task 4.1)
  const isDraft = !selectedFeature?.id || selectedFeature?.isDraft;

  // Query for feature attachments
  const { data: attachmentsData, isLoading: attachmentsLoading } = useQuery({
    queryKey: ['featureAttachments', selectedFeature?.id],
    queryFn: () => execute(featureAttachmentsQuery, { featureId: selectedFeature!.id }),
    enabled: !!selectedFeature?.id && !isDraft,
  });

  const attachments = attachmentsData?.featureAttachments ?? [];

  // Delete attachment mutation
  const deleteAttachmentMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      return execute(deleteFeatureAttachmentMutation, { id: attachmentId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureAttachments', selectedFeature?.id] });
    },
  });

  // Create attachment mutation
  const createAttachmentMutation = useMutation({
    mutationFn: async ({ attachmentId, label }: { attachmentId: string; label: string }) => {
      return execute(createFeatureAttachmentMutation, {
        input: {
          featureId: selectedFeature!.id,
          attachmentId,
          label,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureAttachments', selectedFeature?.id] });
      _resetUploadDialog();
    },
    onError: (error) => {
      toast.error(t('featureProperties.failedCreateAttachment', { error: error.message }));
    },
  });

  const _resetUploadDialog = () => {
    setUploadDialogOpen(false);
    setUploadLabel('');
    setUploadFile(null);
    setIsUploading(false);
  };

  const _handleUpload = async () => {
    if (!uploadFile || !selectedFeature?.id) return;

    setIsUploading(true);

    const toastId = toast.loading(t('featureProperties.uploadingAttachment'));
    try {
      // Generate checksum
      const buffer = await uploadFile.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const byteArray = new Uint8Array(hashBuffer);
      let binary = '';
      for (let i = 0; i < byteArray.length; i++) {
        binary += String.fromCharCode(byteArray[i]);
      }
      const checksum = btoa(binary);

      // Create attachment record and get presigned URL
      const data = await execute(createAttachment, {
        project: projectId,
        filename: uploadFile.name,
        contentType: uploadFile.type || 'application/octet-stream',
        checksumSHA256Base64: checksum,
        type: 'FEATURE_ATTACHMENT',
      });

      const { attachment, uploadUrl } = data?.createAttachment || {};
      if (!attachment?.id || !uploadUrl) {
        throw new Error('Failed to create attachment');
      }

      // Upload file to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: uploadFile,
        headers: {
          'Content-Type': uploadFile.type || 'application/octet-stream',
          'X-Amz-Checksum-Sha256': checksum,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      // Link attachment to feature
      await createAttachmentMutation.mutateAsync({
        attachmentId: attachment.id,
        label: uploadLabel || uploadFile.name,
      });
      analytics.capture('Feature Attachment Uploaded');
      toast.success(t('featureProperties.attachmentUploaded'), { id: toastId });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload: ${error instanceof Error ? error.message : 'Unknown error'}`, { id: toastId });
      setIsUploading(false);
    }
  };

  const _handleDeleteAttachment = async (attachment: FeatureAttachment) => {
    await toast
      .promise(deleteAttachmentMutation.mutateAsync(attachment.id), {
        loading: t('featureProperties.deletingAttachment'),
        success: t('featureProperties.attachmentDeleted'),
        error: t('featureProperties.failedDeleteAttachment'),
      })
      .unwrap();
  };

  const _handleDownloadAttachment = async (attachment: FeatureAttachment) => {
    const toastId = toast.loading(t('featureProperties.downloadingAttachment'));
    try {
      const url = attachment.attachment.url;
      const filename = attachment.attachment.filename ?? 'download';

      const response = await fetch(url, {
        credentials: 'include',
      });

      // Optional: basic error handling
      if (!response.ok) {
        console.error('Failed to fetch file', response.status, response.statusText);
        toast.error(t('featureProperties.failedDownloadAttachment'), { id: toastId });
        return;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(blobUrl);

      toast.success(t('featureProperties.attachmentDownloaded'), { id: toastId });
    } catch (error) {
      toast.error(t('featureProperties.failedDownloadAttachment'), { id: toastId });
      console.error('Failed to download attachment', error);
    }
  };

  const _formatFileSize = (bytes: number | null | undefined) => {
    if (!bytes) return '';
    if (bytes < 1000) return `${bytes} B`;
    if (bytes < 1000 * 1000) return `${(bytes / 1000).toFixed(1)} KB`;
    return `${(bytes / (1000 * 1000)).toFixed(1)} MB`;
  };

  useEffect(() => {
    // Skip fetching properties for draft features
    if (!selectedFeature || isDraft) return;
    execute(featureQuery, {
      id: selectedFeature.id,
    }).then((data) => {
      setSelectedFeature((prev) =>
        !prev
          ? prev
          : {
              ...prev,
              properties: data.feature.properties,
            },
      );
    });
  }, [selectedFeature?.id]);

  const _handleClose = () => {
    setSelectedFeature(null);
    clearHistory();
    // The drawer owns the alignment highlight it opened with — see GeoJson's
    // click handler, which sets it alongside `selectedFeatureAtom`.
    setSelectedAlignmentKey(null);
  };

  // Track moving state in a ref so the onClose callback always sees the latest value
  // (vaul fires onClose via a stale closure when open transitions to false)
  const isMovingRef = useRef(false);
  isMovingRef.current = !!selectedFeature?.isMoving;

  const _handleDrawerClose = () => {
    if (!isMovingRef.current) {
      setSelectedFeature(null);
      clearHistory();
    }
  };

  const _handleSave = async () => {
    if (!selectedFeature || !selectedFeature.onSave) return;

    // Guard: if the user somehow triggers this while a surface re-edit is in
    // progress, refuse to save. The drag state lives on `addSurfaceMode.draftCorners`,
    // not on `selectedFeature.geometry`, so this path would persist stale geometry.
    // Force-close the surface tool so the user gets a clean state.
    if (
      addSurfaceMode.enabled &&
      addSurfaceMode.editFeatureId !== null &&
      addSurfaceMode.editFeatureId === selectedFeature.id
    ) {
      setAddSurfaceMode({ ...DEFAULT_ADD_SURFACE_MODE });
      setSelectedFeature((prev) => (prev ? { ...prev, isMoving: false } : prev));
      return;
    }

    await selectedFeature.onSave(selectedFeature);

    // Close drawer for saved features (draft features handle their own closing in onSave)
    if (!isDraft) {
      _handleClose();
    }
  };

  const _handleDelete = async () => {
    if (!selectedFeature) return;
    await execute(deleteFeatureMutation, { id: selectedFeature.id });
    queryClient.invalidateQueries({ queryKey: ['features', selectedFeature.layerId] });
    _handleClose();
  };

  const _handleMove = () => {
    if (!selectedFeature) return;
    setSelectedFeature({
      ...selectedFeature,
      isMoving: true,
    });
  };

  const isSurfaceFeature =
    selectedFeature?.geometry?.type === 'Polygon' &&
    (selectedFeature?.properties as Record<string, any> | undefined)?.featureType === 'surface';

  // ── Stockpile-feature detection ──────────────────────────────────────────
  // `parseStockpileProperties` reads both the new flat layout and the
  // legacy nested layout (saved before the property flatten); the result
  // is always the flat shape, which is what `StockpilePropertiesView`
  // consumes. Returns null for non-stockpile features → drives the
  // custom-view vs generic-loop branch below.
  const stockpileProps = parseStockpileProperties(selectedFeature?.properties);
  const isStockpileFeature =
    selectedFeature?.geometry?.type === 'Polygon' && stockpileProps !== null;

  const pipeProps = parsePipeProperties(selectedFeature?.properties);
  const isPipeFeature = selectedFeature?.geometry?.type === 'LineString' && pipeProps !== null;

  /**
   * "Edit pipe" on a saved PIPE feature → re-open the pipe detection panel in
   * edit mode, with the centerline + radius pre-loaded so the user can drag the
   * endpoints / adjust radius and re-save. Reconstructs the draft from the
   * LineString geometry + stored properties.
   */
  const _handleEditPipe = () => {
    if (!selectedFeature || !pipeProps) return;
    const geom = selectedFeature.geometry as { type?: string; coordinates?: number[][] } | null;
    if (!geom || geom.type !== 'LineString' || !geom.coordinates || geom.coordinates.length < 2) return;
    const vertices: Vec3[] = geom.coordinates.map((c) => [c[0], c[1], c[2] ?? 0]);
    const a = vertices[0];
    const b = vertices[vertices.length - 1];
    const dirx = b[0] - a[0];
    const diry = b[1] - a[1];
    const dirz = b[2] - a[2];
    const dlen = Math.hypot(dirx, diry, dirz);
    const axis: Vec3 = dlen > 1e-9 ? [dirx / dlen, diry / dlen, dirz / dlen] : [pipeProps.axisX, pipeProps.axisY, pipeProps.axisZ];
    enterPipeEditMode({
      featureId: selectedFeature.id,
      sourceLayerId: pipeProps.sourceLayerId,
      vertices,
      axis,
      radius: pipeProps.radiusM,
      arcCoverageDeg: pipeProps.arcCoverageDeg,
      rmsM: pipeProps.rmsM,
      inlierCount: pipeProps.inlierCount,
      confidence: pipeProps.confidence,
    });
    _handleClose();
  };

  // ── Alignment section (LineString / MultiLineString only) ───────────────
  const geomType = selectedFeature?.geometry?.type;
  const isLineFeature = geomType === 'LineString' || geomType === 'MultiLineString';
  const alignmentProps = parseFeatureAlignmentProps(selectedFeature?.properties);
  const registeredAlignments = useAtomValue(alignmentsAtom);
  const drapeSources = useAtomValue(alignmentDrapeSourcesAtom);
  const allLayers = useAtomValue(layersAtom);
  const setWalk = useSetAtom(alignmentWalkAtom);
  const registeredKey = selectedFeature ? `${selectedFeature.layerId}:${selectedFeature.id}` : '';
  const isRegistered = registeredAlignments.has(registeredKey);

  const _setAlignmentProps = (next: FeatureAlignmentProps | null) => {
    if (!selectedFeature) return;
    const props = { ...((selectedFeature.properties || {}) as Record<string, unknown>) };
    if (next === null) delete props.alignment;
    else props.alignment = next;
    setSelectedFeature({ ...selectedFeature, properties: props });
  };
  const _patchAlignment = (patch: Partial<FeatureAlignmentProps>) => _setAlignmentProps({ ...(alignmentProps ?? {}), ...patch });
  const _walkThis = () => {
    if (!isRegistered) return;
    // Soft paywall: the button stays clickable and opens the upgrade dialog.
    const walkGate = featureCheck('viewer.alignmentWalk');
    if (!walkGate.allowed) {
      walkGate.promptUpgrade();
      return;
    }
    setWalk({ key: registeredKey, station: 0, step: DEFAULT_WALK_STEP, snapKeyPoints: false, follow: true });
    _handleClose();
  };

  // ── Rainfall-feature detection ──────────────────────────────────────────
  // The rainfall worker emits a `kind` discriminator on every feature
  // (pond / flowLine / watershed). When set we render a kind-specific
  // structured view (volumes, fill times, chain position, …) and skip
  // the generic property loop — same pattern as stockpile features.
  const rainfallParsed = parseRainfallFeature(
    (selectedFeature?.properties as Record<string, unknown> | null | undefined) ?? null,
  );
  const isRainfallFeature = rainfallParsed !== null;

  // ── Generic-polygon detection ────────────────────────────────────────────
  // Any other Polygon feature can be the *input* to a fresh stockpile compute
  // (the user already has a polygon they care about — surveyed boundary, drawn
  // outline, anything — they shouldn't have to redraw it). The
  // "Calculate stockpile volume" button runs the same compute panel using the
  // existing polygon, with `editFeatureId === null` so Save mints a fresh
  // stockpile rather than overwriting the source feature.
  const isComputableNonStockpilePolygon =
    selectedFeature?.geometry?.type === 'Polygon' && !isStockpileFeature && !isSurfaceFeature;

  /**
   * Extracts the polygon ring out of `selectedFeature.geometry`, drops the
   * closing duplicate vertex, ensures the ring has proper Z values
   * (defaulting missing Zs to 0), and returns null if the geometry is
   * malformed. Shared between the Re-compute and Calculate-from-polygon paths.
   */
  const getPolygonRing = (): Array<[number, number, number]> | null => {
    const geom = selectedFeature?.geometry as { type?: string; coordinates?: number[][][] } | null;
    if (!geom || geom.type !== 'Polygon' || !geom.coordinates?.[0]) return null;
    const ring = geom.coordinates[0];
    const stripped =
      ring.length > 1 &&
      ring[0][0] === ring[ring.length - 1][0] &&
      ring[0][1] === ring[ring.length - 1][1]
        ? ring.slice(0, -1)
        : ring;
    if (stripped.length < 3) return null;
    return stripped.map((v) => [v[0], v[1], v[2] ?? 0] as [number, number, number]);
  };

  const _handleEditSurfaceGeometry = () => {
    if (!selectedFeature || !isSurfaceFeature) return;
    const ring = (selectedFeature.geometry as any).coordinates?.[0] as Array<[number, number, number]> | undefined;
    if (!ring || ring.length < 4 || !selectedFeature.layerId) return;
    // The drawer hides via the isReEditingThisSurface early-return — no need to
    // also flip isMoving, which would trigger the FeatureMoveActions floating
    // Cancel/Save overlay (intended for the Point move flow).
    setAddSurfaceMode({
      ...DEFAULT_ADD_SURFACE_MODE,
      enabled: true,
      targetLayerId: selectedFeature.layerId,
      phase: 'adjusting',
      pickedPoints: [],
      draftCorners: ring.slice(0, 4) as Array<[number, number, number]>,
      editFeatureId: selectedFeature.id,
    });
  };

  const _handleEdit = () => {
    if (!selectedFeature) return;
    setSelectedFeature({
      ...selectedFeature,
      isEditing: true,
    });
  };

  /**
   * "Re-compute" on a saved STOCKPILE feature → opens the compute panel in
   * edit mode with the existing polygon + parameters pre-loaded. The drawer
   * closes itself so the panel's Sheet has the side surface to itself.
   */
  const _handleRecomputeStockpile = () => {
    // `stockpileProps` is the truthy guard — narrows the union for the
    // sourceLayerId access below. (`isStockpileFeature` is just an alias
    // for `stockpileProps !== null`, but TS can't follow that.)
    if (!selectedFeature || !stockpileProps) return;
    const ring = getPolygonRing();
    if (!ring) return;
    const { sourceLayerId } = stockpileProps;
    if (!sourceLayerId) {
      toast.error(tStockpile('stockpile.errorNoSourcePco'));
      return;
    }
    enterStockpileEditMode({
      featureId: selectedFeature.id,
      polygonRing: ring,
      sourceLayerId,
    });
    _handleClose();
  };

  /**
   * "Calculate stockpile volume" on any other Polygon feature → opens the
   * compute panel using that polygon, but as a *fresh* compute (no
   * editFeatureId — Save creates a new stockpile rather than overwriting the
   * source feature).
   *
   * The source PCO has to come from somewhere — for picked polygons it's set
   * by the first vertex hit; for pre-existing polygons we pick the first
   * currently-visible POINTCLOUD layer. If there's exactly one we go straight
   * in; if there are multiple we still pick the first but warn (the user can
   * Discard and toggle off the wrong PCO if needed); if there's none we
   * refuse with an error.
   *
   * A nicer v2 would surface a source-PCO picker inside the panel itself, but
   * for v1 the auto-pick covers the common case (one cloud loaded).
   */
  const _handleCalculateFromPolygon = () => {
    // Starting a stockpile compute from an already existing polygon is the
    // Pro-only convenience path; drawing a fresh stockpile stays on the base
    // 'viewer.stockpile' gate.
    const gate = featureCheck('viewer.stockpileFromSurface');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    if (!selectedFeature || !isComputableNonStockpilePolygon) return;
    const ring = getPolygonRing();
    if (!ring) return;
    // Read the live PCO atom — only contains PCOs actually loaded in the
    // scene. Filtering layers by `isHidden` is wrong here: a hidden child
    // PCO toggled off via the eye icon stays `isHidden=false` on the
    // server-side record (it's removed from `selectedLayerIds` instead),
    // and an earlier version of this code wrongly counted it as visible.
    const visiblePcoLayerIds = pcoTargets
      .map((p) => p.userData?.layerId as string | undefined)
      .filter((id): id is string => !!id);
    if (visiblePcoLayerIds.length === 0) {
      toast.error(tStockpile('stockpile.errorNoVisiblePco'));
      return;
    }
    if (visiblePcoLayerIds.length > 1) {
      const firstName =
        layers.find((l) => l.id === visiblePcoLayerIds[0])?.name ?? visiblePcoLayerIds[0];
      toast.warning(tStockpile('stockpile.warnMultiplePcos', { name: firstName }));
    }
    // Fresh compute — write the tool atom directly so editFeatureId stays null
    // (Save will mint a new stockpile feature rather than overwriting this
    // source polygon).
    setStockpileToolMode({
      enabled: true,
      phase: 'closed',
      pickedPoints: ring,
      sourceLayerId: visiblePcoLayerIds[0],
      editFeatureId: null,
    });
    _handleClose();
  };

  const layerAncestry = useMemo(() => getLayerAncestry(layer, layers), [layer, layers]);

  // Collect all aliases from the ancestry, starting from root to child,
  // so that the closest (child-most) alias is used when duplicate exists (override upwards).
  const aliases = [...layerAncestry.reverse(), layer]
    .filter(Boolean)
    .flatMap((l) => l!.propertyAliases || [])
    .reduce(
      (acc, alias) => {
        acc[alias.property] = alias.alias;
        return acc;
      },
      {} as Record<string, string>,
    );

  const getPropertyName = (property: string) => {
    return aliases[property] || property;
  };

  // Whether the user is currently allowed to mutate this feature's properties
  // (add new keys, rename existing keys, delete keys, or edit scalar values).
  // Mirrors the per-input `disabled` gate further down: drafts are always
  // editable; saved features only when the user has clicked Edit.
  const canEditProps = isDraft || !!selectedFeature?.isEditing;

  // Pull the cached features to power property-name autocomplete. This MUST
  // use the same loader (and therefore the same cached array shape) as the
  // Viewer/MapView feature queries — they share the `['features', layerId]`
  // key, so returning a different shape here (e.g. the raw `{ featuresByLayer }`
  // object) would poison their cache and make the layer's features vanish.
  const { data: layerFeaturesData } = useQuery({
    queryKey: ['features', selectedFeature?.layerId],
    queryFn: () => loadFeaturesByLayerOfflineAware([selectedFeature!.layerId], projectId!),
    enabled: !!selectedFeature?.layerId && !isDraft && canEditProps,
  });

  // Suggest property keys that exist on *other* features in this layer but
  // not on the current one — i.e. "make this feature look like the rest".
  // Blacklisted keys are excluded so we don't surface OGR_STYLE etc.
  const availablePropertyKeys = useMemo(() => {
    const currentKeys = new Set(Object.keys(selectedFeature?.properties || {}));
    const set = new Set<string>();
    for (const f of layerFeaturesData ?? []) {
      const props = (f.properties as Record<string, unknown> | null) || {};
      for (const k of Object.keys(props)) {
        if (propertyBlacklist.includes(k)) continue;
        if (currentKeys.has(k)) continue;
        set.add(k);
      }
    }
    return [...set].sort();
  }, [layerFeaturesData, selectedFeature?.properties]);

  const _renameProperty = (oldKey: string, rawNewKey: string) => {
    if (!selectedFeature) return;
    const newKey = rawNewKey.trim();
    if (!newKey || newKey === oldKey) return;
    const props = (selectedFeature.properties || {}) as Record<string, unknown>;
    if (newKey in props) {
      toast.error(t('featureProperties.propertyExists', { key: newKey }));
      return;
    }
    // Rebuild preserving original insertion order so the row doesn't jump
    // to the bottom on rename.
    const entries = Object.entries(props).map(
      ([k, v]) => (k === oldKey ? [newKey, v] : [k, v]) as [string, unknown],
    );
    setSelectedFeature({
      ...selectedFeature,
      properties: Object.fromEntries(entries),
    });
  };

  const _deleteProperty = (key: string) => {
    if (!selectedFeature) return;
    const next = { ...((selectedFeature.properties || {}) as Record<string, unknown>) };
    delete next[key];
    setSelectedFeature({ ...selectedFeature, properties: next });
  };

  const _addProperty = (rawKey: string, value: string) => {
    if (!selectedFeature) return;
    const key = rawKey.trim();
    if (!key) return;
    const props = (selectedFeature.properties || {}) as Record<string, unknown>;
    if (key in props) {
      toast.error(t('featureProperties.propertyExists', { key }));
      return;
    }
    setSelectedFeature({
      ...selectedFeature,
      properties: { ...props, [key]: value },
    });
  };

  // While the user is re-editing this surface's geometry via the AddSurfaceTool,
  // the drawer's Save/Cancel footer would compete with the toolbar's Save changes
  // button — and worse, the drawer Save would persist stale geometry because the
  // live drag state lives on addSurfaceMode.draftCorners, not on selectedFeature.geometry.
  // So unmount the drawer entirely during that flow (vaul keeps interactive bits
  // around during a close animation when we'd just toggle `open`).
  const isReEditingThisSurface =
    addSurfaceMode.enabled &&
    addSurfaceMode.editFeatureId !== null &&
    !!selectedFeature?.id &&
    addSurfaceMode.editFeatureId === selectedFeature.id;

  if (isReEditingThisSurface) return null;

  // Suppress the drawer while any creation tool is active — every creation
  // tool now owns a top-middle FloatingToolbar with OK / Cancel, so the
  // drawer's bottom-right Save / Cancel would be redundant and would let
  // the user save through a path that doesn't reset the tool's mode atom.
  // We force the `open` prop to false rather than early-returning null —
  // unmounting vaul's Drawer mid-session leaves a cleanup half-state that
  // surfaces as "Cannot convert undefined or null to object" / "Cannot
  // assign to read only property 'position'" when the next tool's 3D
  // markers mount in the same frame.
  const anyCreationToolActive =
    addPointMode.enabled || addLineMode.enabled || addPolygonMode.enabled;

  return (
    <Drawer
      open={!anyCreationToolActive && !!selectedFeature && !selectedFeature.isMoving}
      onClose={_handleDrawerClose}>
      <DrawerContent>
        <DrawerHeader className='w-lg'>
          <DrawerTitle className='text-foreground'>
            {/* Use the stockpile name as the title when we're showing a saved
                STOCKPILE feature — much more useful than the generic
                "Selected feature" label. */}
            {isDraft
              ? t('featureProperties.newFeature')
              : stockpileProps
                ? stockpileProps.name.trim() || tStockpile('stockpile.untitled')
                : t('featureProperties.selectedFeature')}
          </DrawerTitle>
          {!isDraft && <FeatureNavigator className='mt-2' />}
          {/* Jump-to-feature: flies the 3D viewer camera onto the
              selected feature's centroid. Reuses the same
              `viewerEvents.emit('jumpToTarget', …)` event the attribute
              table's context-menu uses, so the camera behaviour stays
              consistent across surfaces. Hidden for draft features
              (no persisted geometry yet) and for features with no
              geometry coordinates. */}
          {!isDraft && selectedFeature?.geometry && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='mt-2 w-full'
              onClick={() => {
                const geom = selectedFeature.geometry as { coordinates?: any } | undefined;
                if (!geom?.coordinates) return;
                // Walk the coordinate tree and average all leaf
                // [x, y, z?] triples. This handles Point /
                // LineString / Polygon / Multi* uniformly without
                // per-type branching — same logic as the attribute
                // table's `handleJumpToFeature`.
                const flat: [number, number, number][] = [];
                const visit = (val: any) => {
                  if (Array.isArray(val) && typeof val[0] === 'number') {
                    flat.push([val[0], val[1], val[2] ?? 0]);
                  } else if (Array.isArray(val)) {
                    val.forEach(visit);
                  }
                };
                visit(geom.coordinates);
                if (flat.length === 0) return;
                const cx = flat.reduce((s, p) => s + p[0], 0) / flat.length;
                const cy = flat.reduce((s, p) => s + p[1], 0) / flat.length;
                const cz = flat.reduce((s, p) => s + p[2], 0) / flat.length;
                viewerEvents.emit('jumpToTarget', { x: cx, y: cy, z: cz });
              }}>
              <Crosshair className='size-4' />
              {t('featureProperties.jumpToFeature')}
            </Button>
          )}
        </DrawerHeader>
        <div className='flex-1 overflow-y-auto'>
          <div className='flex flex-col gap-4 px-4 py-2'>
            <div className='flex flex-col gap-1'>
              <Label>
                <div className='flex gap-1'>
                  {t('featureProperties.sgid')}
                  <InfoCircleSolid data-tooltip-id='feature-serial-tooltip' className='size-3.5 opacity-60' />
                </div>
              </Label>
              <ID value={selectedFeature?.serial?.toString() || ''} />
            </div>
            <div className='flex flex-col gap-1'>
              <Label>
                <div className='flex gap-1'>
                  {t('featureProperties.layer')}
                  <InfoCircleSolid data-tooltip-id='feature-layer-tooltip' className='size-3.5 opacity-60' />
                </div>
              </Label>
              <ID value={layer?.name || ''} />
            </div>
            <PortalTooltip id='feature-serial-tooltip'>
              {t('featureProperties.sgidTooltip')}
            </PortalTooltip>
            <PortalTooltip id='feature-layer-tooltip'>
              {t('featureProperties.layerTooltip')}
            </PortalTooltip>
            {selectedFeature?.geometry.type === 'Point' && (
              <div className='flex flex-col gap-1'>
                <Label>
                  {t('featureProperties.coordinates')} {isDraft && <span className='text-xs font-normal opacity-70'>{t('featureProperties.readOnly')}</span>}
                </Label>
                <Coords coords={selectedFeature.geometry.coordinates} />

                <PortalTooltip id='coordinates-tooltip'>
                  <span className='text-sm font-medium'>
                    {t('featureProperties.coordinates')} <span className='text-xs font-medium opacity-70'>{t('featureProperties.clickToCopy')}</span>
                  </span>
                  <div className='font-mono leading-tight mt-0.5'>
                    <div>X: {selectedFeature.geometry.coordinates[0].toFixed(6)}</div>
                    <div>Y: {selectedFeature.geometry.coordinates[1].toFixed(6)}</div>
                    <div>Z: {selectedFeature.geometry.coordinates[2].toFixed(6)}</div>
                  </div>
                </PortalTooltip>
              </div>
            )}
            {/* Stockpile-feature custom view. The generic property loop (below)
                renders nested-object values as the literal "[object Object]"
                — a STOCKPILE feature has `result`, `basePlane`, `material`,
                `mass` all as objects, which made the drawer unreadable. We
                short-circuit the loop entirely for these features and render
                a structured summary + Re-compute button. */}
            {/* Both stockpile blocks gated on the PostHog flag — see
                isStockpileAnalysisEnabled at the top of the component. */}
            {stockpileProps && isStockpileAnalysisEnabled && (
              <StockpilePropertiesView
                props={stockpileProps}
                onRecompute={_handleRecomputeStockpile}
                onClose={_handleClose}
                /* The typed `t` from i18next exposes a string-literal-union of
                   known keys; passing it through a generic `(key: string, opts?) => string`
                   prop signature widens the key type, which i18next-typed
                   rejects. Cast through `any` at the boundary — runtime `t`
                   accepts any string. */
                t={tStockpile as any}
              />
            )}

            {/* "Calculate stockpile volume" button for any other Polygon
                feature (drawn outline, surveyed boundary, etc.). Lets the
                user run a stockpile compute on a polygon they already have
                without redrawing it. */}
            {isComputableNonStockpilePolygon && isStockpileAnalysisEnabled && (
              <div className='flex flex-col gap-2 mt-1'>
                <Button onClick={_handleCalculateFromPolygon} variant='outline'>
                  {tStockpile('stockpile.calculateFromPolygon')}
                </Button>
                <p className='text-xs text-slate-600 dark:text-stone-400'>
                  {tStockpile('stockpile.calculateFromPolygonHelp')}
                </p>
              </div>
            )}

            {/* "Edit pipe" for a saved PIPE feature — re-opens the detection
                panel in edit mode (drag endpoints / adjust radius / re-save).
                The generic property loop below still renders the flat pipe
                metrics, so this is purely an extra action. */}
            {isPipeFeature && (
              <div className='flex flex-col gap-2 mt-1'>
                <Button onClick={_handleEditPipe} variant='outline'>
                  {tStockpile('pipe.panelTitleEdit')}
                </Button>
              </div>
            )}

            {isLineFeature && !isRainfallFeature && (
              <div className='flex flex-col gap-2 mt-1 rounded-md border border-slate-200 dark:border-stone-700 p-3'>
                <div className='flex items-center justify-between'>
                  <Label htmlFor='feature-alignment-toggle'>{t('alignment.useAsAlignment')}</Label>
                  <Switch
                    id='feature-alignment-toggle'
                    checked={alignmentProps !== null}
                    onCheckedChange={(on) => _setAlignmentProps(on ? {} : null)}
                    disabled={!canEditProps}
                  />
                </div>
                {alignmentProps !== null && (
                  <>
                    <div className='flex flex-col gap-1'>
                      <Label htmlFor='feature-alignment-name'>{t('alignment.name')}</Label>
                      <Input
                        id='feature-alignment-name'
                        value={alignmentProps.name ?? ''}
                        placeholder={`Feature #${selectedFeature?.serial ?? ''}`}
                        onChange={(e) => _patchAlignment({ name: e.target.value })}
                        disabled={!canEditProps}
                      />
                    </div>
                    <div className='flex flex-col gap-1'>
                      <Label>{t('alignment.staStart')}</Label>
                      <NumericField value={alignmentProps.staStart ?? 0} onChange={(v) => _patchAlignment({ staStart: v })} step='1' disabled={!canEditProps} />
                    </div>
                    <div className='flex items-center justify-between'>
                      <Label htmlFor='feature-alignment-reverse'>{t('alignment.reverse')}</Label>
                      <Switch id='feature-alignment-reverse' checked={!!alignmentProps.reverse} onCheckedChange={(v) => _patchAlignment({ reverse: v })} disabled={!canEditProps} />
                    </div>
                    <div className='flex flex-col gap-1'>
                      <Label>{t('alignment.drapeOn')}</Label>
                      <Select
                        value={alignmentProps.drapeLayerId ?? '__none'}
                        onValueChange={(v) => _patchAlignment({ drapeLayerId: v === '__none' ? null : v })}
                        disabled={!canEditProps}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='__none'>{t('alignment.drapeNone')}</SelectItem>
                          {[...drapeSources.keys()].map((layerId) => (
                            <SelectItem key={layerId} value={layerId}>
                              {allLayers.find((l) => l.id === layerId)?.name ?? layerId}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant='outline' onClick={_walkThis} disabled={!isRegistered} title={isRegistered ? undefined : t('alignment.saveFirst')}>
                      {t('alignment.walk')}
                    </Button>
                    {!isRegistered && <p className='text-xs text-slate-600 dark:text-stone-400'>{t('alignment.saveFirst')}</p>}
                  </>
                )}
              </div>
            )}

            {/* Rainfall-analysis features get a kind-specific structured
                view (pond fill time / chain position / flow line peak
                runoff / watershed area) and skip the generic loop —
                otherwise the worker's nested numeric stats render
                without context. Mirrors the stockpile handling above. */}
            {isRainfallFeature && rainfallParsed && (
              <RainfallFeatureView
                parsed={rainfallParsed}
                stormDurationH={
                  ((layer?.metadata as Record<string, any> | undefined)?.rainfallSummary
                    ?.storm?.durationH) as number | undefined
                }
                featureId={selectedFeature?.id}
                featureLayerId={selectedFeature?.layerId ?? undefined}
              />

            )}

            {/* Generic property loop. Skipped for STOCKPILE features
                regardless of feature-flag state (the loop renders nested
                objects as the literal "[object Object]", which is worse
                than nothing). When the flag is off, stockpile features show
                neither the custom view nor the broken generic loop — the
                drawer falls back to just SGID + layer + attachments.
                Also skipped for rainfall features (the structured view
                above already surfaces the relevant fields, and the raw
                kind/pondId/chainHead values aren't actionable). */}
            {!isStockpileFeature &&
              !isRainfallFeature && (
                <>
                  {Object.entries(selectedFeature?.properties || {})
                    .filter(([key]) => !propertyBlacklist.includes(key))
                    .map(([key, value]) => {
                      const label = getPropertyName(key);
                      // Nested object/array values can't sensibly be edited
                      // through a single-line text input — typing into the
                      // JSON would just replace the whole structure with a
                      // string. Force the input read-only so we surface the
                      // value (via `formatPropertyValue`, no more
                      // `[object Object]`) without inviting accidental
                      // corruption. Scalars stay editable as before.
                      const isObjectValue = value !== null && typeof value === 'object';
                      // Protected keys can't be renamed or removed (ID is
                      // commonly relied on by downstream tooling). Their
                      // value input is also read-only as before.
                      const isProtected = key === 'ID';
                      return (
                        <EditablePropertyRow
                          key={key}
                          propertyKey={key}
                          label={label}
                          value={value}
                          canEdit={canEditProps}
                          isObjectValue={isObjectValue}
                          isProtected={isProtected}
                          availableKeys={availablePropertyKeys}
                          onValueChange={(newValue) => {
                            if (!selectedFeature) return;
                            setSelectedFeature({
                              ...selectedFeature,
                              properties: {
                                ...selectedFeature.properties,
                                [key]: newValue,
                              },
                            });
                          }}
                          onRename={(newKey) => _renameProperty(key, newKey)}
                          onDelete={() => _deleteProperty(key)}
                          t={t}
                        />
                      );
                    })}

                  {canEditProps && (
                    <AddPropertyRow
                      availableKeys={availablePropertyKeys}
                      onAdd={_addProperty}
                      t={t}
                    />
                  )}
                </>
              )}

            {/* Attachments Section */}
            {!isDraft && (
              <div className='flex flex-col gap-2 mt-4'>
                <Label>{t('featureProperties.attachments')}</Label>
                {attachmentsLoading ? (
                  <div className='flex items-center gap-2 text-muted-foreground text-sm'>
                    <Loader2 className='size-4 animate-spin' />
                    {t('featureProperties.loadingAttachments')}
                  </div>
                ) : attachments.length === 0 ? (
                  <p className='text-muted-foreground text-sm'>{t('featureProperties.noAttachments')}</p>
                ) : (
                  <div className='flex flex-col gap-2'>
                    {attachments.map((attachment) => (
                      <>
                        <div
                          key={attachment.id}
                          className='max-w-120 flex items-center justify-between gap-2 p-2 pl-3 border border border-slate-200/70 bg-slate-50/40 dark:border-stone-900 dark:bg-stone-900/20 rounded-md'>
                          <div className='flex items-center gap-2 flex-1 min-w-0 overflow-hidden'>
                            <div className='flex flex-col min-w-0 leading-tight'>
                              <span className='text-sm font-medium truncate'>{attachment.label}</span>
                              <div className='flex items-center font-medium leading-tight text-xs gap-1 text-slate-700 dark:text-stone-300'>
                                <span
                                  className='flex items-center gap-1 whitespace-nowrap overflow-hidden flex-shrink-0'
                                  data-tooltip-id={`attachment-tooltip-${attachment.id}`}>
                                  <img
                                    src={
                                      attachment.uploadedBy?.avatar?.url ||
                                      getPlaceholderAvatar(
                                        attachment.uploadedBy?.email || attachment.uploadedBy?.id || '',
                                      )
                                    }
                                    alt={attachment.uploadedBy?.name || attachment.uploadedBy?.email || t('featureProperties.unknown')}
                                    className='size-4 rounded-full object-cover border border-slate-200/70 dark:border-stone-800/70'
                                  />{' '}
                                  {attachment.uploadedBy?.name || attachment.uploadedBy?.email || t('featureProperties.unknown')}
                                </span>
                                &middot;
                                <span className='text-xs truncate opacity-70'>{attachment.attachment.filename}</span>
                              </div>
                            </div>
                          </div>
                          <div className='flex items-center'>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => _handleDownloadAttachment(attachment)}
                              disabled={deleteAttachmentMutation.isPending}
                              className='aspect-square p-0!'>
                              <Download className='size-4 stroke-2' />
                            </Button>
                            {selectedFeature?.isEditing && hasRole('EDITOR') && (
                              <Button
                                variant='ghost'
                                size='sm'
                                color='red'
                                onClick={() => _handleDeleteAttachment(attachment)}
                                disabled={deleteAttachmentMutation.isPending}
                                className='aspect-square p-0!'>
                                <Trash className='size-4 stroke-2' />
                              </Button>
                            )}
                          </div>
                        </div>
                        <PortalTooltip id={`attachment-tooltip-${attachment.id}`}>
                          <div className='flex items-center gap-1'>
                            <span>{t('featureProperties.uploaded', { date: formatDateTime(attachment.createdAt) })}</span>
                            <span className='flex items-center gap-1'>
                              <img
                                src={
                                  attachment.uploadedBy?.avatar?.url ||
                                  getPlaceholderAvatar(attachment.uploadedBy?.email || attachment.uploadedBy?.id || '')
                                }
                                alt={attachment.uploadedBy?.name || attachment.uploadedBy?.email || t('featureProperties.unknown')}
                                className='size-4 rounded-full object-cover border border-slate-200/70 dark:border-stone-800/70'
                              />{' '}
                              {attachment.uploadedBy?.name || attachment.uploadedBy?.email || t('featureProperties.unknown')}
                            </span>
                          </div>
                        </PortalTooltip>
                      </>
                    ))}
                  </div>
                )}
                {selectedFeature?.isEditing && hasRole('EDITOR') && (
                  <Button variant='outline' onClick={() => setUploadDialogOpen(true)} className='mt-1'>
                    <Plus className='size-4 mr-1' />
                    {t('featureProperties.uploadAttachment')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('featureProperties.uploadAttachment')}</DialogTitle>
              <DialogDescription>{t('featureProperties.uploadAttachmentDescription')}</DialogDescription>
            </DialogHeader>
            <div className='flex flex-col gap-4 py-4'>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='attachment-label'>{t('featureProperties.label')}</Label>
                <Input
                  id='attachment-label'
                  placeholder={t('featureProperties.labelPlaceholder')}
                  value={uploadLabel}
                  onChange={(e) => setUploadLabel(e.target.value)}
                />
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='attachment-file'>{t('featureProperties.file')}</Label>
                <Input id='attachment-file' type='file' onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
                {uploadFile && (
                  <p className='text-sm text-muted-foreground'>
                    {t('featureProperties.selectedFile', { name: uploadFile.name, size: _formatFileSize(uploadFile.size) })}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={_resetUploadDialog} disabled={isUploading}>
                {t('featureProperties.cancel')}
              </Button>
              <Button onClick={_handleUpload} disabled={!uploadFile || isUploading}>
                {isUploading ? (
                  <>
                    <Loader2 className='size-4 mr-2 animate-spin' />
                    {t('featureProperties.uploading')}
                  </>
                ) : (
                  t('featureProperties.upload')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <DrawerFooter>
          {/* Sub-task 4.1: Conditionally render buttons based on draft status */}
          {isDraft ? (
            // Draft feature buttons
            <>
              <Button variant='default' onClick={_handleSave}>
                {t('featureProperties.createFeature')}
              </Button>
              <Button variant='link' onClick={_handleClose}>
                {t('featureProperties.cancel')}
              </Button>
            </>
          ) : (
            // Existing saved feature buttons
            <>
              {hasRole('EDITOR') &&
              selectedFeature?.geometry.type &&
              !isSurfaceFeature &&
              ['Point', 'LineString', 'MultiLineString', 'Polygon', 'MultiPolygon'].includes(
                selectedFeature.geometry.type,
              ) ? (
                <Button variant='outline' onClick={_handleMove} disabled={layer?.isLocked}>
                  {t('featureProperties.moveFeature')}
                </Button>
              ) : null}
              {hasRole('EDITOR') && isSurfaceFeature ? (
                <Button variant='outline' onClick={_handleEditSurfaceGeometry} disabled={layer?.isLocked}>
                  {t('featureProperties.editSurfaceGeometry')}
                </Button>
              ) : null}
              {hasRole('EDITOR') && (
                <>
                  {selectedFeature?.isEditing ? (
                    <Button variant='default' onClick={_handleSave}>
                      {t('featureProperties.save')}
                    </Button>
                  ) : (
                    <Button
                      data-tooltip-id={layer?.isLocked ? 'feature-properties-locked' : undefined}
                      disabled={!selectedFeature || layer?.isLocked}
                      variant='outline'
                      onClick={_handleEdit}>
                      {t('featureProperties.edit')}
                    </Button>
                  )}
                </>
              )}
              {hasRole('EDITOR') && (
                <>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        data-tooltip-id={layer?.isLocked ? 'feature-properties-locked' : undefined}
                        disabled={!selectedFeature || layer?.isLocked}
                        variant='destructive'>
                        {t('featureProperties.delete')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('featureProperties.confirmDeleteTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('featureProperties.confirmDeleteDescription')}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('featureProperties.cancel')}</AlertDialogCancel>
                        <AlertDialogAction variant='destructive' onClick={_handleDelete}>
                          {t('featureProperties.delete')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
              <PortalTooltip id='feature-properties-locked'>
                {t('featureProperties.layerLockedTooltip')}
              </PortalTooltip>
              <Button variant='link' onClick={_handleClose}>
                {t('featureProperties.cancel')}
              </Button>
            </>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default FeatureProperties;

// ────────────────────────────────────────────────────────────────────────────
// STOCKPILE feature — custom properties view
// ────────────────────────────────────────────────────────────────────────────

/**
 * Structured renderer for STOCKPILE feature properties. Replaces the generic
 * key/value loop (which would show `result` and `basePlane` as the literal
 * "[object Object]") with formatted volume / area / mass / coverage rows.
 *
 * Hides the mass row entirely when no material is set (instead of showing
 * "null" or an empty Input). Surfaces a Re-compute button as the primary
 * action — this is the only way to refresh the values after an underlying
 * point cloud changes.
 */
const StockpilePropertiesView = ({
  props,
  onRecompute,
  onClose,
  t,
}: {
  props: StockpileFeatureProperties;
  onRecompute: () => void;
  onClose: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) => {
  // Mass falls back to volume × density when the persisted massKg is null
  // (older saves that didn't persist mass; "none" material with a custom
  // density would also leave massKg null).
  const massKg =
    props.massKg != null
      ? props.massKg
      : props.densityKgPerM3 != null
        ? props.volumeM3 * props.densityKgPerM3
        : null;

  // labelKey isn't persisted any more — we re-derive it from materialId.
  // `getMaterial` returns the canonical record (or `none`); its labelKey
  // is the i18n key for `stockpile.materials.*`.
  const materialLabelKey =
    props.materialId !== 'none' ? getMaterial(props.materialId).labelKey : null;

  const fmtNum = (v: number | undefined, fractionDigits: number, suffix: string): string =>
    typeof v === 'number' && Number.isFinite(v)
      ? `${v.toLocaleString(undefined, { maximumFractionDigits: fractionDigits })} ${suffix}`
      : '—';

  const computedAt =
    props.computedAt > 0 ? new Date(props.computedAt).toLocaleString() : null;

  return (
    <section className='flex flex-col gap-3'>
      {/* Volume — the headline metric. Big, accent colour, can't be missed. */}
      <div className='rounded-md border border-orange-300 dark:border-orange-700/60 bg-orange-50 dark:bg-orange-950/30 p-3'>
        <div className='text-xs uppercase tracking-wide text-orange-700 dark:text-orange-300'>
          {t('stockpile.resultVolume')}
        </div>
        <div className='text-2xl font-bold text-orange-700 dark:text-orange-200'>
          {fmtNum(props.volumeM3, 2, 'm³')}
        </div>
        {massKg != null && (
          <div className='mt-1 text-sm text-orange-700 dark:text-orange-200'>
            {massKg >= 1000
              ? `${(massKg / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} t`
              : `${massKg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg`}
            {materialLabelKey && (
              <span className='opacity-70'>
                {' · '}
                {t(`stockpile.materials.${materialLabelKey}` as any)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Stats grid. Plain key/value rows — no inputs (the underlying values
          aren't user-editable; they're the result of compute). */}
      <div className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
        <Row label={t('stockpile.resultArea')} value={fmtNum(props.areaM2, 1, 'm²')} />
        <Row label={t('stockpile.resultMeanHeight')} value={fmtNum(props.meanHeightM, 2, 'm')} />
        <Row label={t('stockpile.resultMaxHeight')} value={fmtNum(props.maxHeightM, 2, 'm')} />
        <Row label={t('stockpile.fieldCellSize')} value={fmtNum(props.cellSize, 2, 'm')} />
        <Row label={t('stockpile.resultSampleCount')} value={props.sampleCount.toLocaleString()} />
        <Row
          label={t('stockpile.resultMissCount')}
          value={
            props.sampleCount + props.missCount > 0
              ? `${props.missCount.toLocaleString()} (${Math.round(
                  (props.missCount / (props.sampleCount + props.missCount)) * 100,
                )}%)`
              : '—'
          }
        />
        <Row
          label={t('stockpile.fieldSamplingQuality')}
          value={t(`stockpile.samplingQuality.${props.samplingQuality}`)}
        />
        {computedAt && <Row label={t('stockpile.computedAt')} value={computedAt} />}
      </div>

      {/* Re-compute is the only "edit" action for stockpiles. Discard / Save
          live in the StockpileComputePanel sheet that opens after click. */}
      <div className='flex gap-2 mt-2'>
        <Button variant='default' onClick={onRecompute} className='flex-1'>
          {t('stockpile.recomputeButton')}
        </Button>
        <Button variant='outline' onClick={onClose}>
          {t('stockpile.close')}
        </Button>
      </div>
    </section>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className='flex flex-col'>
    <span className='text-xs uppercase tracking-wide text-slate-500 dark:text-stone-400'>{label}</span>
    <span className='text-sm font-medium text-slate-800 dark:text-stone-100'>{value}</span>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Editable property rows — generic GeoJSON property key/value pairs
// ────────────────────────────────────────────────────────────────────────────

/**
 * Combobox that lets the user pick a property name from the
 * `availableKeys` list (keys observed on other features in the same layer)
 * OR type a brand-new key the layer hasn't seen yet. The "Use" row at the
 * bottom commits arbitrary typed input — without it the user could only
 * choose from existing suggestions, which defeats the point of supporting
 * extensible properties.
 */
const PropertyNameCombobox = ({
  initialValue,
  availableKeys,
  open,
  onOpenChange,
  onCommit,
  triggerLabel,
  t,
}: {
  initialValue: string;
  availableKeys: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCommit: (value: string) => void;
  triggerLabel: ReactNode;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) => {
  const [search, setSearch] = useState('');

  // Reset the search field every time the popover reopens — otherwise
  // stale text from a previous open would seed the input.
  useEffect(() => {
    if (open) setSearch(initialValue);
  }, [open, initialValue]);

  const trimmed = search.trim();
  const filtered = trimmed
    ? availableKeys.filter((k) => k.toLowerCase().includes(trimmed.toLowerCase()))
    : availableKeys;
  const exactMatch = availableKeys.includes(trimmed);

  const commit = (value: string) => {
    const v = value.trim();
    if (!v) return;
    onCommit(v);
    onOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{triggerLabel}</PopoverTrigger>
      <PopoverContent className='p-0 w-[280px]' align='start'>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('featureProperties.searchProperty')}
            value={search}
            onValueChange={setSearch}
            onKeyDown={(e) => {
              // Enter on the empty list (or with a brand-new value) commits
              // the typed text — Command's default Enter only fires when an
              // item is highlighted, so we handle the no-suggestion case here.
              if (e.key === 'Enter' && filtered.length === 0 && trimmed) {
                e.preventDefault();
                commit(trimmed);
              }
            }}
          />
          <CommandList>
            {filtered.length === 0 && !trimmed && (
              <CommandEmpty>{t('featureProperties.noSuggestions')}</CommandEmpty>
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((k) => (
                  <CommandItem
                    key={k}
                    value={k}
                    onSelect={() => commit(k)}>
                    <span className='font-mono text-xs'>{k}</span>
                    {k === initialValue && <Check className='ml-auto size-4' />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {trimmed && !exactMatch && (
              <CommandGroup>
                <CommandItem value={`__use__${trimmed}`} onSelect={() => commit(trimmed)}>
                  <Plus className='size-4' />
                  <span>
                    {t('featureProperties.useNewKey', { key: trimmed })}
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const EditablePropertyRow = ({
  propertyKey,
  label,
  value,
  canEdit,
  isObjectValue,
  isProtected,
  availableKeys,
  onValueChange,
  onRename,
  onDelete,
  t,
}: {
  propertyKey: string;
  label: string;
  value: unknown;
  canEdit: boolean;
  isObjectValue: boolean;
  isProtected: boolean;
  availableKeys: string[];
  onValueChange: (newValue: string) => void;
  onRename: (newKey: string) => void;
  onDelete: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) => {
  const [renameOpen, setRenameOpen] = useState(false);
  const showAliasIcon = propertyKey !== label;
  const valueDisabled = isProtected || isObjectValue || !canEdit;

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between gap-1 min-h-6'>
        <Label className='flex items-center gap-1 min-w-0'>
          <span className='truncate'>{label}</span>
          {showAliasIcon && (
            <LabelSolid
              data-tooltip-id={`property-label-tooltip-${propertyKey}`}
              className='size-3.5 opacity-60 shrink-0'
            />
          )}
        </Label>
        {canEdit && !isProtected && (
          <div className='flex items-center gap-0.5'>
            <PropertyNameCombobox
              initialValue={propertyKey}
              availableKeys={availableKeys}
              open={renameOpen}
              onOpenChange={setRenameOpen}
              onCommit={onRename}
              t={t}
              triggerLabel={
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='size-6 p-0 opacity-60 hover:opacity-100'
                  data-tooltip-id={`property-rename-tooltip-${propertyKey}`}
                  aria-label={t('featureProperties.renameProperty')}>
                  <Pencil className='size-3.5' />
                </Button>
              }
            />
            <PortalTooltip id={`property-rename-tooltip-${propertyKey}`}>
              {t('featureProperties.renameProperty')}
            </PortalTooltip>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='size-6 p-0 opacity-60 hover:opacity-100 hover:text-red-600'
              data-tooltip-id={`property-delete-tooltip-${propertyKey}`}
              aria-label={t('featureProperties.removeProperty')}
              onClick={onDelete}>
              <Trash className='size-3.5' />
            </Button>
            <PortalTooltip id={`property-delete-tooltip-${propertyKey}`}>
              {t('featureProperties.removeProperty')}
            </PortalTooltip>
          </div>
        )}
      </div>
      <PortalTooltip id={`property-label-tooltip-${propertyKey}`}>
        {t('featureProperties.aliasFor')} <strong className='font-semibold'>{propertyKey}</strong>
      </PortalTooltip>
      <Input
        disabled={valueDisabled}
        value={formatPropertyValue(value)}
        onChange={(e) => onValueChange(e.target.value)}
      />
    </div>
  );
};

const AddPropertyRow = ({
  availableKeys,
  onAdd,
  t,
}: {
  availableKeys: string[];
  onAdd: (key: string, value: string) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) => {
  const [draftKey, setDraftKey] = useState('');
  const [draftValue, setDraftValue] = useState('');
  const [open, setOpen] = useState(false);
  const [comboOpen, setComboOpen] = useState(false);

  const reset = () => {
    setDraftKey('');
    setDraftValue('');
    setOpen(false);
  };

  const submit = () => {
    if (!draftKey.trim()) return;
    onAdd(draftKey, draftValue);
    reset();
  };

  if (!open) {
    return (
      <Button
        variant='outline'
        size='sm'
        className='self-start mt-1'
        onClick={() => setOpen(true)}>
        <Plus className='size-4 mr-1' />
        {t('featureProperties.addProperty')}
      </Button>
    );
  }

  return (
    <div className='flex flex-col gap-2 rounded-md border border-dashed border-slate-300 dark:border-stone-700 p-3'>
      <Label className='text-xs uppercase tracking-wide opacity-70'>
        {t('featureProperties.addProperty')}
      </Label>
      <PropertyNameCombobox
        initialValue={draftKey}
        availableKeys={availableKeys}
        open={comboOpen}
        onOpenChange={setComboOpen}
        onCommit={(key) => setDraftKey(key)}
        t={t}
        triggerLabel={
          <Button
            type='button'
            variant='outline'
            role='combobox'
            className={cn(
              'w-full justify-between font-mono',
              !draftKey && 'text-muted-foreground font-normal',
            )}>
            <span className='truncate'>
              {draftKey || t('featureProperties.propertyName')}
            </span>
            <ChevronsUpDown className='size-4 opacity-50' />
          </Button>
        }
      />
      <Input
        placeholder={t('featureProperties.propertyValue')}
        value={draftValue}
        onChange={(e) => setDraftValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
      />
      <div className='flex gap-2 justify-end'>
        <Button variant='ghost' size='sm' onClick={reset}>
          <X className='size-4 mr-1' />
          {t('featureProperties.cancel')}
        </Button>
        <Button variant='default' size='sm' onClick={submit} disabled={!draftKey.trim()}>
          <Plus className='size-4 mr-1' />
          {t('featureProperties.add')}
        </Button>
      </div>
    </div>
  );
};

